import React from "react"
import {toastr} from "react-redux-toastr"
import ReactTable, {
    ComponentPropsGetter0,
    ExpandedRows,
    Filter,
    FilteredChangeFunction,
    PageChangeFunction,
    PageSizeChangeFunction,
    RowInfo,
    SortingRule,
} from "react-table"
import {
    Button,
    Checkbox,
    CheckboxProps,
    Popup,
    Segment,
} from "semantic-ui-react"
import {
    $setProductIsDelete,
    $activateProducts,
    $validateProducts,
    $getProductRefVentes,
} from "../../core/requests"
import {
    ISupplierReference,
    IReferenceIds,
    getReferenceIdsFromList,
    getReferenceIds,
    IRawSupplierReference,
    ISupplierInfo,
    IRawSupplierDeclinedReference,
    completeRawDeclined,
    completeDeclined,
} from "../../models"
import {IProductRefVente} from "../../models/product/productRefVente"
import {convertToBoolean, Languages, Roles} from "../../utils"
import {deleteDoublonComplex, deleteDoublons} from "../../utils/doublon-tools"
import {
    getCurrentPage,
    getPageSize,
    LocalStorageColumnsKeys,
    LocalStorageKeys,
} from "../../utils/local-storage-keys"
import {ensureCorrectPopupPosition} from "../../utils/popup-tools"
import {stringify} from "../../utils/stringifyier"
import {
    topRefTableUtils,
    defaultFilterMethod,
    getTranslations,
} from "../../utils/table/methods"
import Loading from "../components/com_loading"
import MultiModifModal from "../components/com_multi-modif-modal"
import MultiModifPricesModal from "../components/com_multi-modif-prices-modal"
import {getColumns, getRefVenteIndex} from "../components/com_product-list-columns"
import {useEventCallback} from "../../utils/memo"
import {IFieldState} from "../product-form/_reducers/_utils/product-form-types"

interface IProductListProps {
    activeLang: Languages
    products: IRawSupplierReference[]
    isgUrl: string
    role: Roles
    comRawData: any[]
    translations: any
    supplier: ISupplierInfo
    loading: boolean
    idSocieteg: string

    fetchProducts(): Promise<boolean>

    goToProduct(droopyId: string): void

    goToProductDeclined(droopyId: string, declined: string): void
}

const returnNull = () => null

const ProductList = ({
                         products,
                         isgUrl,
                         activeLang,
                         comRawData,
                         translations,
                         role,
                         supplier,
                         loading: l,
                         idSocieteg,
                         goToProduct,
                         goToProductDeclined,
                         fetchProducts,
                     }: IProductListProps) => {
    // prettier-ignore
    const defaultShowImageValue: boolean = React.useMemo(
        () => JSON.parse(
            localStorage.getItem(LocalStorageKeys.SHOW_IMAGES) || "true"
        ), [])
    const [selectedRefs, setSelectedRefs] = React.useState<IReferenceIds[]>([])
    const [refVentes, setRefVentes] = React.useState<IProductRefVente[]>([])
    const [expandedRefs, setExpandedRefs] = React.useState<ExpandedRows>({})
    const [loading, setLoadin] = React.useState(false)
    const [showDeletedRefs, setShowDeletedRefs] = React.useState(false)
    const [showOnlyNewProducts, setShowOnlyNewProducts] = React.useState(false)
    const [showImages, setShowImages] = React.useState(defaultShowImageValue)
    const [selectedActivatedFilter, setSelectedActivatedFilter] = React.useState<
        "All" | "1" | "0"
    >("All")
    const [selectedValidatedFilter, setSelectedValidatedFilter] = React.useState<
        "All" | "True" | "False"
    >("All")
    const [displayedData, setDisplayedData] = React.useState<
        ISupplierReference[]
    >([])
    const [pageSize, setPageSize] = React.useState(
        getPageSize(supplier.supplierName, 20)
    )
    const [currentPage, setCurrentPage] = React.useState(
        getCurrentPage(supplier.supplierName, 0)
    )

    React.useEffect(() => {
        refresh()
    }, [])

    React.useEffect(() => {
        if (idSocieteg) {
            fetchRefVentes()
        }
    }, [idSocieteg])

    React.useEffect(() => {
        setPageSize(getPageSize(supplier.supplierName, 20))
        setCurrentPage(getCurrentPage(supplier.supplierName, 0))
    }, [supplier.supplierName])

    const setLoading = (value: boolean, origin: string) => {
        setLoadin(value)
    }

    const handleShowImageClick = () => {
        setShowImages((prev) => {
            window.localStorage.setItem(
                LocalStorageKeys.SHOW_IMAGES,
                JSON.stringify(!prev)
            )
            return !prev
        })
    }

    // The selection is read by the table cells through a ref: the columns (and every resolved row) no longer
    // depend on it.
    const selectedIds = React.useMemo(
        () => new Set(selectedRefs.map((r) => r.droopyId)),
        [selectedRefs]
    )
    const selectedIdsRef = React.useRef(selectedIds)
    selectedIdsRef.current = selectedIds
    const isSelected = React.useCallback(
        (droopyId: string) => selectedIdsRef.current.has(droopyId),
        []
    )

    const handleChangeSelectedRefs = useEventCallback((
        row: IRawSupplierReference,
        checked: boolean
    ) => {
        const reference = row as unknown as ISupplierReference
        const refIds = getReferenceIds(reference)
        // if (refData.length <= 0) {
        //   toastr.warning(translations.cantSelectIncompleteProduct, "")
        //   return
        // }
        let newSelectedList: IReferenceIds[]
        const variantsRawList =
            reference.declined
                ?.filter(
                    (x) =>
                        (showDeletedRefs && x.deleted) || (!showDeletedRefs && !x.deleted)
                )
                .map((x) => completeDeclined(reference, x)) || []
        const variantIds = getReferenceIdsFromList(variantsRawList)
        const topRefRaw = finalRefs.find((r) => r.supplierRef === reference.topRef)

        const addTopRef =
            topRefRaw !== undefined &&
            (topRefRaw.declined || []).every(
                (r: IRawSupplierDeclinedReference) =>
                    selectedIdsRef.current.has(r.droopyId) || refIds.droopyId === r.droopyId
            )
        const topRefIds = addTopRef ? [getReferenceIds(topRefRaw)] : []

        if (checked) {
            newSelectedList = deleteDoublonComplex(
                [...selectedRefs, ...variantIds, refIds, ...topRefIds],
                "droopyId"
            )
        } else {
            const removed = new Set(
                [refIds, ...variantIds].map((p) => stringify(p))
            )
            newSelectedList = selectedRefs.filter((r) => !removed.has(stringify(r)))
        }
        setSelectedRefs(newSelectedList)
    })

    const subComp = (a: RowInfo) => {
        const ref = a.original as IRawSupplierReference
        if (ref.isTopRef) {
            return topRefTableUtils.columnsToJsx(
                subColumns,
                ref,
                goToProduct,
                showDeletedRefs
            )
        }

        return null
    }

    const [columns, subColumns] = React.useMemo(
        () => [
            getColumns({
                isgUrl,
                isSelected,
                onToggleSelection: handleChangeSelectedRefs,
                refVentes,
                activeLang,
                rawComTree: comRawData,
                translations,
                showImages,
                showDeletedRefs,
                idSocieteg,
                supplierName: supplier.supplierName,
                setSelectedValidatedFilter,
                selectedValidatedFilter,
                setSelectedActivatedFilter,
                selectedActivatedFilter,
            }),
            getColumns({
                isgUrl,
                isSelected,
                onToggleSelection: handleChangeSelectedRefs,
                refVentes,
                activeLang,
                rawComTree: comRawData,
                isSubComponent: true,
                translations,
                showImages,
                showDeletedRefs,
                idSocieteg,
                supplierName: supplier.supplierName,
                setSelectedValidatedFilter,
                selectedValidatedFilter,
                setSelectedActivatedFilter,
                selectedActivatedFilter,
            }),
        ],
        // References only: the trees, translations and sales references are replaced when reloaded. Their
        // serialization ran on every render of the list (FE-PERF-R03).
        [
            selectedValidatedFilter,
            selectedActivatedFilter,
            activeLang,
            comRawData,
            translations,
            showImages,
            showDeletedRefs,
            idSocieteg,
            refVentes,
            isgUrl,
            supplier.supplierName,
        ]
    )

    const handleDeleteButtonClick = () => {
        setLoading(true, "handleDeleteButtonClick")
        const refsToSend = selectedRefs.filter((p) =>
            products.find(
                (pr) =>
                    (pr.supplierRef === p.supplierRef &&
                        (showDeletedRefs ? pr.deleted : !pr.deleted)) ||
                    (pr.supplierRef === p.topRef &&
                        pr.declined?.find(
                            (d) =>
                                d.supplierRef === p.supplierRef &&
                                (showDeletedRefs ? d.deleted : !d.deleted)
                        ))
            )
        )
        return $setProductIsDelete(
            refsToSend,
            !showDeletedRefs,
            role,
            supplier.supplierName
        )
            // The error is displayed by $myFetch; refresh in both cases to show the real state.
            .catch(() => undefined)
            .then(() => fetchProducts())
            .finally(() => {
                setSelectedRefs([])
                setLoading(false, "handleDeleteButtonClick")
            })
    }

    const handleActivateButtonClick = (activate: boolean) => () => {
        setLoading(true, "handleActivateButtonClick")

        // const validated = products
        //   .filter(
        //     (o) =>
        //       o.supplierRef &&
        //       selectedRefs.map((e) => e.supplierRef).includes(o.supplierRef)
        //   )
        //   .map((o) => o.validated)

        const supplierRefs = selectedRefs
            .filter((x) => !x.topRef)
            .map((o) => o.supplierRef)
        const declinedSupplierRefs = selectedRefs
            .filter((x) => x.topRef)
            .map((o) => o.supplierRef)
        $activateProducts(supplierRefs, declinedSupplierRefs, activate, role)
            // The error is displayed by $myFetch; always release the loader and refresh the real state.
            .catch(() => undefined)
            .finally(() => {
                setLoading(false, "handleActivateButtonClick")
                setSelectedRefs([])
                fetchProducts()
            })
    }

    const handleShowDeletedProductButtonClick = () => {
        setSelectedRefs([])
        setShowDeletedRefs(!showDeletedRefs)
        setShowOnlyNewProducts(false)
    }

    const handleShowNewProductsButtonClick = () => {
        setSelectedRefs([])
        setShowDeletedRefs(false)
        setShowOnlyNewProducts(!showOnlyNewProducts)
    }

    const onClickValidateProducts = () => {
        setLoading(true, "onClickValidateProducts")
        const findProduct = (supplierRef: string) =>
            products.find((p) => p.supplierRef === supplierRef)
        // Declined references live inside their top reference, not at the first level of the list.
        const findDeclined = (s: { supplierRef: string; topRef?: string }) =>
            findProduct(s.topRef || "")?.declined?.find((d) => d.supplierRef === s.supplierRef)
        const topRefsOf = (declined: { topRef?: string }[]) =>
            declined.map((c) => c.topRef).filter((c) => c) as string[]

        const toValidateDeclined = deleteDoublonComplex(
            selectedRefs.filter((s) => s.supplierRef.length > 0 && s.topRef),
            "supplierRef"
        )
        const selectedRefsOnly = selectedRefs
            .filter((s) => s.supplierRef.length > 0 && !s.topRef)
            .map((o) => o.supplierRef)
        const toValidate = deleteDoublons([
            ...selectedRefsOnly,
            ...topRefsOf(toValidateDeclined),
        ])

        // Top references are always flagged as validated: only normal references and declined references carry a
        // meaningful validation state.
        const selectedNormalRefs = selectedRefsOnly.filter((r) => !findProduct(r)?.isTopRef)
        const notValidatedRefs = selectedNormalRefs.filter((r) => !findProduct(r)?.validated)
        const onlyNotValidatedDeclined = toValidateDeclined.filter((s) => !findDeclined(s)?.validated)
        const onlyNotValidated = deleteDoublons([
            ...notValidatedRefs,
            ...topRefsOf(onlyNotValidatedDeclined),
        ])
        const hasAlreadyValidated =
            notValidatedRefs.length !== selectedNormalRefs.length ||
            onlyNotValidatedDeclined.length !== toValidateDeclined.length

        if (hasAlreadyValidated) {
            toastr.confirm(translations.revalidateAlreadyValidated, {
                onOk: () =>
                    validateProducts(
                        toValidate,
                        toValidateDeclined.map((o) => o.supplierRef)
                    ),
                onCancel: () =>
                    validateProducts(
                        onlyNotValidated,
                        onlyNotValidatedDeclined.map((o) => o.supplierRef)
                    ),
                // @ts-ignore
                okText: "Oui",
                // @ts-ignore
                cancelText: "Non",
            })
        } else if (toValidate.length > 0 || toValidateDeclined.length > 0) {
            validateProducts(
                toValidate,
                toValidateDeclined.map((o) => o.supplierRef)
            )
        } else {
            setLoading(false, "onClickValidateProducts")
        }
    }

    const validateProducts = (
        toValidate: string[],
        toValidateDeclined: string[]
    ) => {
        setLoading(false, "onClickValidateProducts")
        if (toValidate.length <= 0 && toValidateDeclined.length <= 0) {
            return
        }
        $validateProducts(
            toValidate,
            toValidateDeclined,
            role,
            supplier.supplierName,
            translations
        )
            // The error is displayed by $myFetch; refresh in both cases to show the real state.
            .catch(() => undefined)
            .then(() => fetchProducts())
    }

    const refresh = () => {
        fetchProducts()
    }

    const fetchRefVentes = () => {
        $getProductRefVentes(idSocieteg, role).then((res) => setRefVentes(res))
    }

    const onFieldLooseFocus = (field: IFieldState<any>) => {
        // checkFieldValidity(permanentFormIds.MULTI_MODIF, field)
    }

    const checkAllIndeter =
        selectedRefs.length > 0 && selectedRefs.length < displayedData.length

    const checkAllChecked = selectedRefs.length === displayedData.length

    const onCheckAllProducts = () => {
        const allRawRefDatas = checkAllChecked ? [] : displayedData
        // const allRefDatas = allRawRefDatas.filter((r) => r.length > 0)
        setSelectedRefs(getReferenceIdsFromList(allRawRefDatas))
    }

    const checkAllStyle = {
        position: "absolute",
        top: 92,
        left: 51,
        margin: 0,
        zIndex: 5,
    }

    const finalRefs: IRawSupplierReference[] = React.useMemo(() => {
        const refVenteIndex = getRefVenteIndex(refVentes)
        const refVenteByRef = {get: (ref: string) => refVenteIndex.get(ref)?.refVente}
        return products
            .filter(
                (r) =>
                    (!showOnlyNewProducts ||
                        !r.validatedOnce ||
                        (r.declined && r.declined.find((m) => !m.validatedOnce))) &&
                    ((showDeletedRefs &&
                            (r.deleted || (r.declined && r.declined.find((m) => m.deleted)))) ||
                        (!showDeletedRefs &&
                            !r.deleted &&
                            (!r.declined || r.declined.find((m) => !m.deleted))))
            )
            .map(
                (x) =>
                    ({
                        ...x,
                        refVente: refVenteByRef.get(x.supplierRef),
                        declined: x.declined?.map(
                            (d) =>
                                ({
                                    ...d,
                                    refVente: refVenteByRef.get(d.supplierRef),
                                } as IRawSupplierDeclinedReference)
                        ),
                    } as IRawSupplierReference)
            )
    }, [products, showDeletedRefs, showOnlyNewProducts, refVentes])

    const defaultSorted: SortingRule[] = React.useMemo(() => [
        {
            id: LocalStorageColumnsKeys.DATE_MAJ,
            desc: true,
        },
    ], [])

    // Read once: react-table only uses the default filters at mount (and re-applies them when they differ, which
    // re-filtered the whole list after each filter change). The current filters are kept by the table itself.
    // Empty filters are not restored: they filter nothing and only cost a pass over the whole list.
    const storedFilters = React.useMemo(() => [
        {
            id: LocalStorageColumnsKeys.SUPPLIER_REF,
            value: localStorage.getItem(LocalStorageColumnsKeys.SUPPLIER_REF) || "",
        },
        {
            id: LocalStorageColumnsKeys.LABEL,
            value: localStorage.getItem(LocalStorageColumnsKeys.LABEL) || "",
        },
        {
            id: LocalStorageColumnsKeys.COM_TREE,
            value: localStorage.getItem(LocalStorageColumnsKeys.COM_TREE) || "",
        },
        {
            id: LocalStorageColumnsKeys.PRICE,
            value: localStorage.getItem(LocalStorageColumnsKeys.PRICE) || "",
        },
        {
            id: LocalStorageColumnsKeys.EAN,
            value: localStorage.getItem(LocalStorageColumnsKeys.EAN) || "",
        },
        {
            id: LocalStorageColumnsKeys.MANUFACTURER_REF,
            value:
                localStorage.getItem(LocalStorageColumnsKeys.MANUFACTURER_REF) || "",
        },
        {
            id: LocalStorageColumnsKeys.DATE_CREAT,
            value: localStorage.getItem(LocalStorageColumnsKeys.DATE_CREAT) || "",
        },
        {
            id: LocalStorageColumnsKeys.DATE_MAJ,
            value: localStorage.getItem(LocalStorageColumnsKeys.DATE_MAJ) || "",
        },
        {
            id: LocalStorageColumnsKeys.REF_VENTE,
            value: localStorage.getItem(LocalStorageColumnsKeys.REF_VENTE) || "",
        },
    ].filter((storedFilter) => storedFilter.value !== ""), [])

    const onFilterChange: FilteredChangeFunction = (
        newFiltering: Filter[],
        column: any,
        value: any
    ) => {
        newFiltering.forEach((o) => {
            localStorage.setItem(o.pivotId || o.id, o.value || "")
        })
        if (newFiltering.find((o) => o.id === column.id) === undefined) {
            localStorage.setItem(column.id, "")
        }
    }

    // The rows displayed after filtering/sorting (used by "select all"). react-table keeps the same `sortedData`
    // array until the data, the columns, the filters or the sorting change: flatten it only then, and update the
    // state after the render instead of comparing two JSON serializations of the catalogue on every render
    // (FE-PERF-R04).
    const lastSortedDataRef = React.useRef<any[] | null>(null)
    const handleSortedDataChange = (sortedData: any[]) => {
        if (sortedData === lastSortedDataRef.current) {
            return
        }
        lastSortedDataRef.current = sortedData
        const flattened: ISupplierReference[] = sortedData
            .map((o) => o._original as ISupplierReference)
            .map((p) =>
                p.isTopRef
                    ? [
                        p,
                        ...(p?.declined || []).map((x) => completeDeclined(p, x)), // TODO
                    ]
                    : p
            )
            .flat()
        Promise.resolve().then(() => setDisplayedData(flattened))
    }

    const handleUnselectIncompleteProducts = () => {
        setSelectedRefs(selectedRefs.filter((d) => d.supplierRef))
    }
    const areIncompleteSelected =
        selectedRefs.filter((o) => o.supplierRef.length < 1).length > 0
    const validatePopupContent = showDeletedRefs ? (
        <>{translations.cantValidateDeleted}</>
    ) : (
        <>
            {translations.incompleteSelected + "  "}
            <Button
                onClick={handleUnselectIncompleteProducts}
                compact
                basic
                color="violet"
            >
                {translations.unselectIncomplete}
            </Button>
        </>
    )

    const [hasSelectedHeadButNotEveryDeclined, hasSelectedDeclinedButNotHead] = React.useMemo(() => {
        const head = selectedRefs.some((s) =>
            s.isTopRef
                ? products.some(
                (p) =>
                    p.topRef === s.supplierRef &&
                    !p.deleted &&
                    !selectedIds.has(p.droopyId)
            )
                : false
        )
        const declined = selectedRefs.some((s) =>
            s.topRef && !s.isTopRef
                ? products.some(
                (p) =>
                    s.topRef === p.supplierRef &&
                    p.deleted &&
                    !selectedIds.has(p.droopyId)
            )
                : false
        )
        return [head, declined]
    }, [selectedRefs, selectedIds, products])

    const preventDeleteButtonClick = showDeletedRefs
        ? hasSelectedDeclinedButNotHead
        : hasSelectedHeadButNotEveryDeclined

    const deletePopupContent = preventDeleteButtonClick ? (
        <>
            {showDeletedRefs
                ? translations.restoreHeadWarning
                : translations.deleteHeadWarning}
        </>
    ) : (
        <>
            {translations.areYouSure + "  "}
            <Button
                compact
                icon="check"
                color="green"
                label={
                    (showDeletedRefs
                        ? translations.restoreConfirmationFirstPart
                        : translations.deleteConfirmationFirstPart) +
                    " " +
                    selectedRefs.length +
                    " " +
                    translations.deleteConfirmationSecondPart
                }
                onClick={handleDeleteButtonClick}
            />
        </>
    )

    const handlePageChange: PageChangeFunction = (page: number) => {
        localStorage.setItem(
            LocalStorageKeys.SELECTED_PAGE_ + supplier.supplierName,
            page + ""
        )
        setCurrentPage(page)
    }

    const handlePageSizeChange: PageSizeChangeFunction = (
        newPageSize: number,
        newPage: number
    ) => {
        localStorage.setItem(
            LocalStorageKeys.PAGE_SIZE_ + supplier.supplierName,
            newPageSize + ""
        )
        setPageSize(newPageSize)

        localStorage.setItem(
            LocalStorageKeys.SELECTED_PAGE_ + supplier.supplierName,
            newPage + ""
        )
        setCurrentPage(newPage)
    }

    // console.log({ products, finalRefs })

    return (
        <div className="product-list">
            <Checkbox
                style={checkAllStyle}
                indeterminate={checkAllIndeter}
                checked={checkAllChecked}
                onClick={onCheckAllProducts}
            />
            <ReactTable
                className="product-list-table"
                NoDataComponent={returnNull}
                loading={loading || (l && products.length < 1)}
                data={finalRefs}
                SubComponent={subComp}
                onExpandedChange={setExpandedRefs}
                expanded={expandedRefs}
                columns={columns}
                filterable={true}
                sortable={true}
                showPageJump={true}
                page={currentPage}
                onPageChange={handlePageChange}
                pageSize={pageSize}
                onPageSizeChange={handlePageSizeChange}
                showPagination={true}
                defaultFilterMethod={defaultFilterMethod}
                getTrProps={topRefTableUtils.getTrProps()}
                getTdProps={topRefTableUtils.getTdProps(goToProduct)}
                defaultFiltered={storedFilters}
                onFilteredChange={onFilterChange}
                defaultSorted={defaultSorted}
                {...getTranslations(translations)}
            >
                {(state, makeTable, instance) => {
                    if (currentPage > (instance.state.pages || 1) - 1) {
                        instance.onPageChange((instance.state.pages || 1) - 1)
                    }

                    handleSortedDataChange(state.sortedData)
                    return makeTable()
                }}
            </ReactTable>

            <div className="productListButtons">
                {(role === Roles.MARKET_LEADER && (
                    <div className="button-div left">
                        <Popup
                            {...ensureCorrectPopupPosition}
                            content={deletePopupContent}
                            disabled={selectedRefs.length === 0}
                            hoverable
                            position="top left"
                            flowing
                            on={preventDeleteButtonClick ? "hover" : "click"}
                            trigger={
                                <div className="button-div-button">
                                    <Button
                                        className="element button marketLeaderButton"
                                        disabled={
                                            selectedRefs.length === 0 || preventDeleteButtonClick
                                        }
                                    >
                                        {showDeletedRefs
                                            ? translations.cancelDeleteProductButtonLabel
                                            : translations.deleteProductButtonLabel}
                                    </Button>
                                </div>
                            }
                        />
                        <div className="button-div-button">
                            <Button
                                className="element button marketLeaderButton"
                                onClick={handleShowDeletedProductButtonClick}
                            >
                                {showDeletedRefs
                                    ? translations.showNonDeletedProductsButtonLabel
                                    : translations.showDeletedProductsButtonLabel}
                            </Button>
                        </div>
                        <Popup
                            {...ensureCorrectPopupPosition}
                            content={validatePopupContent}
                            hoverable
                            position="top center"
                            flowing
                            disabled={!areIncompleteSelected && !showDeletedRefs}
                            trigger={
                                <div className="button-div-button">
                                    <Button
                                        disabled={
                                            selectedRefs.length < 1 ||
                                            areIncompleteSelected ||
                                            showDeletedRefs
                                        }
                                        className="element button marketLeaderButton"
                                        onClick={onClickValidateProducts}
                                    >
                                        {translations.validateSeveralProducts}
                                    </Button>
                                </div>
                            }
                        />
                        <div className="button-div-button">
                            <Button
                                className="element button marketLeaderButton"
                                onClick={handleShowNewProductsButtonClick}
                            >
                                {showOnlyNewProducts
                                    ? translations.showAllProducts
                                    : translations.showOnlyNewProducts}
                            </Button>
                        </div>
                    </div>
                )) || <div/>}

                <div className="button-div right">
                    <Segment compact className="showImageSegment">
                        <Checkbox
                            toggle
                            fitted
                            checked={showImages}
                            onClick={handleShowImageClick}
                            label={
                                showImages
                                    ? translations.showImageButtonTrue
                                    : translations.showImageButtonFalse
                            }
                        />
                    </Segment>

                    <div className="button-div-button">
                        <MultiModifPricesModal
                            disabled={selectedRefs.length < 2}
                            selectedRefs={selectedRefs}
                            fullProductList={products}
                            onFieldLooseFocus={onFieldLooseFocus}
                            refVentes={refVentes}
                        />
                    </div>

                    <div className="button-div-button">
                        <Button
                            className="element button colored-button"
                            disabled={selectedRefs.length === 0}
                            onClick={handleActivateButtonClick(true)}
                        >
                            {translations.activateProductButtonLabel}
                        </Button>
                    </div>

                    <div className="button-div-button">
                        <Button
                            className="element button colored-button"
                            disabled={selectedRefs.length === 0}
                            onClick={handleActivateButtonClick(false)}
                        >
                            {translations.deactivateProductButtonLabel}
                        </Button>
                    </div>

                    <div className="button-div-button">
                        <MultiModifModal
                            disabled={selectedRefs.length < 2}
                            selectedRefs={selectedRefs}
                            fullProductList={finalRefs}
                            onFieldLooseFocus={onFieldLooseFocus}
                        />
                    </div>

                    <div className="button-div-button">
                        <Button
                            className="element button colored-button"
                            onClick={refresh}
                            loading={l}
                        >
                            {translations.refreshListButtonLabel}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ProductList
